import { CheckCircleIcon } from '@heroicons/react/solid'
import {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import type { ComponentProps, ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { classes, getParentByTag } from 'src/utils'
import { motion, useMotionValue } from 'framer-motion'

type Props = {
  currentIndex: number
} & (
  | {
      disableNavigation: true
      routes: {
        path: string
        label: string
        icon?: undefined
      }[]
    }
  | {
      disableNavigation?: false
      routes: {
        path: string
        label: string
        icon: ReactElement<ComponentProps<'svg'>>
      }[]
    }
)

export default function Steps(props: Props) {
  const width = useMotionValue(0)
  const translateX = useMotionValue(0)
  const transitionProperty = useMotionValue('none')
  const activeEl = useRef<HTMLAnchorElement>(null)

  useLayoutEffect(() => {
    if (activeEl.current) {
      width.set(activeEl.current.offsetWidth)
      translateX.set(activeEl.current.offsetLeft)
    }
  }, [width, translateX, props.currentIndex])

  useEffect(() => {
    transitionProperty.set('all')
  }, [transitionProperty])

  return (
    <ul className="relative z-10 flex flex-row items-center justify-start h-full select-none">
      {props.currentIndex !== -1 && (
        <motion.div
          className="absolute left-0 h-0.5 transition -bottom-px -z-10 bg-theme-500"
          style={{ width, translateX, transitionProperty }}
        />
      )}

      {props.routes.map((item, index) => (
        <Link
          onClick={(e) => {
            if (!props.disableNavigation) {
              const target = getParentByTag(e.target as HTMLElement, 'a')
              width.set(target?.offsetWidth ?? 0)
              translateX.set(target?.offsetLeft ?? 0)
            }
          }}
          key={item.path}
          to={item.path}
          className={classes(
            'flex flex-row items-center justify-center px-4 py-1 mx-3 -mb-px group rounded',
            !props.disableNavigation && index !== props.currentIndex
              ? 'cursor-pointer hover:bg-slate-200 active:bg-slate-300'
              : 'cursor-default pointer-events-none',
          )}
          tabIndex={props.disableNavigation ? -1 : undefined}
          ref={index === props.currentIndex ? activeEl : undefined}
        >
          {props.disableNavigation
            ? getIcon(index, props.currentIndex, props.disableNavigation)
            : isValidElement(item.icon) &&
              cloneElement(item.icon, {
                'aria-hidden': true,
                className: classes(
                  'h-5 h-5 mr-4',
                  index === props.currentIndex
                    ? 'text-theme-500'
                    : 'text-slate-400',
                ),
              })}

          <span
            className={classes(
              'text-sm font-medium leading-6 capitalize ',
              !props.disableNavigation && 'group-hover:text-slate-600',
              index === props.currentIndex
                ? 'text-theme-600'
                : 'text-slate-500',
            )}
          >
            {item.label}
          </span>
        </Link>
      ))}
    </ul>
  )
}

function getIcon(index: number, currentIndex: number, disabled?: boolean) {
  if (index < currentIndex) {
    return (
      <CheckCircleIcon
        aria-hidden
        className={classes(
          'w-5 h-5 mr-4 text-slate-400',
          !disabled && 'group-hover:text-slate-500 group-active:text-slate-500',
        )}
      />
    )
  }

  return (
    <div
      className={classes(
        'flex items-center justify-center w-5 h-5 mr-4 text-xs font-extrabold rounded-full',
        index === currentIndex
          ? 'bg-theme-500 text-white'
          : 'bg-slate-100 text-slate-400',
        !disabled &&
          index !== currentIndex &&
          'group-hover:bg-slate-400 group-hover:text-slate-100 group-active:bg-slate-500 group-active:text-slate-100',
      )}
    >
      {index + 1}
    </div>
  )
}
